import { useEffect, useRef } from "react"
// Singleton de gestion de la WebSocket notifications : une seule connexion par
// session, partagée par tout composant (le bouton « reconnecter » de la nav agit
// sur la même connexion). Démarré/arrêté par le hook useNotificationsSocket.
//
// ⚠ Garde d'identité : ws.close() est asynchrone — le onclose d'une ancienne
// socket peut arriver APRÈS l'ouverture de la suivante. Chaque handler vérifie
// donc qu'il appartient encore à la socket courante (ws === socket), sinon une
// reconnexion manuelle dupliquait les sockets (onclose tardif : écrasait la
// référence de la nouvelle, dispatchait wsDisconnected à tort et programmait
// une reconnexion en plus → voyant qui clignote, connexions zombies).
import { WS_URL } from "../store/actions/depannageActions"
import {
	wsConnecting,
	wsConnected,
	wsDisconnected,
	nouveauSignalementRecu,
	fetchSignalements,
} from "../store/actions/notificationActions"
import { showToast } from "../store/actions/toastActions"
import {
	fetchFichesOuvertes,
	setFichePrise,
	setFicheDemande,
	clearFicheDemande,
	setFicheDemandeReponse,
} from "../store/actions/ficheOuverteActions"
import { getOpenerId } from "./session"
import { fetchAccess } from "../store/actions/accessActions"

let ws = null
// GE-62 (B3) : écrans abonnés à « mission modifiée » (dispatch).
const abonnesMissions = new Set()
let reconnectTimer = null
let backoff = 1000
let ctx = null // { token, dispatch }

const clearTimer = () => {
	if (reconnectTimer) {
		clearTimeout(reconnectTimer)
		reconnectTimer = null
	}
}

const open = () => {
	if (!ctx) return
	const { token, dispatch } = ctx
	clearTimer()
	dispatch(wsConnecting())
	const socket = new WebSocket(`${WS_URL}ws/notifications?token=${token}`)
	ws = socket

	socket.onopen = () => {
		if (ws !== socket) return // socket remplacée entre-temps
		backoff = 1000
		dispatch(wsConnected())
		// Rattrapage à chaque (re)connexion : signalements actifs + verrous de fiches
		// (au cas où un événement « fiche prise » aurait été manqué hors-ligne).
		dispatch(fetchSignalements())
		dispatch(fetchFichesOuvertes())
		// GE-62 (revue phase B, 🟡 I) : missions modifiées pendant la coupure
		// → les écrans abonnés relisent.
		abonnesMissions.forEach((cb) => cb({ reconnexion: true }))
	}

	socket.onmessage = (e) => {
		if (ws !== socket) return
		let msg
		try {
			msg = JSON.parse(e.data)
		} catch (err) {
			return
		}
		// On vérifie le type AVANT de dispatcher (extensible aux futurs flux).
		switch (msg.event) {
			// "nouveau_signalement" = ancien nom de l'événement (compat pendant la
			// transition) ; "nouvelle_notification" = nom générique, toutes catégories.
			case "nouveau_signalement":
			case "nouvelle_notification": {
				dispatch(nouveauSignalementRecu(msg.data))
				const estSignalement =
					!msg.data.type_notification ||
					msg.data.type_notification === "signalement"
				dispatch(
					showToast(
						"info",
						estSignalement
							? `Nouveau signalement : ${msg.data.categorie || "signalement"}`
							: `Notification : ${msg.data.texte || msg.data.type_notification}`,
					),
				)
				break
			}
			case "eligibilite_modifiee":
				// Le boss vient de changer « qui reçoit quoi » (Configuration →
				// Notifications). Deux choses sont périmées : les droits chargés
				// au boot (cloche, page /notifications) ET les abonnements de
				// CETTE socket, figés à sa connexion côté serveur. On recharge
				// les premiers, puis on relance la socket pour les seconds — et
				// on re-fetch la liste, qui peut s'être élargie ou réduite.
				dispatch(fetchAccess()).then(() => {
					dispatch(fetchSignalements())
					reconnectNotificationsSocket()
				})
				break
			case "mission_modifiee":
				// GE-62 (B3) : une mission vient d'être écrite (web, mobile,
				// assistances…) → les écrans de dispatch montés la rechargent.
				abonnesMissions.forEach((cb) => cb(msg.data))
				break
			case "fiche_prise":
				// Une autre session a pris la main sur une fiche : on rafraîchit les
				// verrous (→ lecture seule immédiate) et, si CET onglet est celui qui
				// perd le contrôle (opener), on déclenche le modal de notification.
				dispatch(fetchFichesOuvertes())
				if (msg.data.opener === getOpenerId()) {
					dispatch(setFichePrise(msg.data))
				}
				break
			case "fiche_demande":
				// Demande de prise de main destinée au DÉTENTEUR : ce onglet doit
				// montrer le popup accepter/refuser (ou le refermer si annulée).
				if (msg.data.detenteur_opener === getOpenerId()) {
					if (msg.data.cancelled) dispatch(clearFicheDemande())
					else dispatch(setFicheDemande(msg.data))
				}
				break
			case "fiche_demande_reponse":
				// Réponse du détenteur destinée au DEMANDEUR. On rafraîchit les verrous
				// (en cas d'acceptation, la main vient de changer) et on notifie l'onglet.
				dispatch(fetchFichesOuvertes())
				if (msg.data.demandeur_opener === getOpenerId()) {
					dispatch(setFicheDemandeReponse(msg.data))
				}
				break
			default:
				// event inconnu → ignoré proprement
				break
		}
	}

	socket.onclose = (e) => {
		// Fermeture d'une socket périmée (déjà remplacée) : ignorer.
		if (ws !== socket) return
		ws = null
		// Le close code dit QUI a raccroché (1006 réseau/proxy, 1011 erreur
		// serveur, 1001 shutdown…) — remonté au reducer pour diagnostic prod.
		dispatch(wsDisconnected({ code: e.code, reason: e.reason || "" }))
		if (!ctx) return
		// eslint-disable-next-line no-console
		console.warn(
			`[notifications] WS fermée (code ${e.code}${
				e.reason ? ` — ${e.reason}` : ""
			}), reconnexion dans ${backoff / 1000}s`,
		)
		// Reconnexion avec backoff exponentiel (plafonné à 30s).
		reconnectTimer = setTimeout(open, backoff)
		backoff = Math.min(backoff * 2, 30000)
	}

	socket.onerror = () => {
		socket.close() // onclose enchaînera (garde d'identité incluse)
	}
}

// Déclarée après `open`/`clearTimer` mais utilisée dans onmessage : les
// fonctions sont hoistées, l'appel n'a lieu qu'à la réception d'un message.
export const startNotificationsSocket = (token, dispatch) => {
	ctx = { token, dispatch }
	backoff = 1000
	open()
}

export const stopNotificationsSocket = () => {
	clearTimer()
	const socket = ws
	ws = null // rend les handlers de l'ancienne socket inertes (garde d'identité)
	if (socket) socket.close()
	if (ctx) ctx.dispatch(wsDisconnected())
	ctx = null
}

// Reconnexion manuelle (bouton de la nav) : repart d'un backoff neuf.
export const reconnectNotificationsSocket = () => {
	if (!ctx) return
	clearTimer()
	backoff = 1000
	const socket = ws
	ws = null // le onclose de l'ancienne socket sera ignoré (garde d'identité)
	if (socket) socket.close()
	open()
}

// GE-62 (B3) : `cb(data)` à chaque mission modifiée ({id, etat, version,
// champs}) ; renvoie la fonction de désabonnement.
export const abonnerMissionsModifiees = (cb) => {
	abonnesMissions.add(cb)
	return () => abonnesMissions.delete(cb)
}

// Revue phase B (🟠5) : un écran de dispatch recharge sa liste quand une
// mission est écrite (regroupé sur `delai` ms ; le sondage reste le filet).
// Revue 5 (🟡) : `filtre(data)` → seulement les missions qui concernent
// l'écran (états de l'onglet, id affiché) ; une reconnexion relit toujours.
export const useMissionsModifiees = (
	recharger,
	{ delai = 600, filtre = null } = {},
) => {
	const ref = useRef(recharger)
	ref.current = recharger
	const filtreRef = useRef(filtre)
	filtreRef.current = filtre
	useEffect(() => {
		let regroupe = null
		const desabonner = abonnerMissionsModifiees((data) => {
			if (
				!(data && data.reconnexion) &&
				filtreRef.current &&
				!filtreRef.current(data || {})
			)
				return
			if (regroupe) return
			regroupe = setTimeout(() => {
				regroupe = null
				ref.current()
			}, delai)
		})
		return () => {
			clearTimeout(regroupe)
			desabonner()
		}
	}, [delai])
}
